'use client';

import type { IconName } from '@polyseme/design-system';

import type { RiskProfile } from './constants';
import type { FormSchema } from './schema';

import {
  FieldWrapper,
  FormField,
  Icon,
  OptionSelector,
  ProjectionChart,
  RadioGroup,
  Select,
  Separator,
  SimpleCard,
  TextInput,
  Typography,
} from '@polyseme/design-system';
import {
  formatCurrency,
  formatPercent,
} from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { DynamicFontTypography, RippleIndicator } from '~/landing/components';
import { useRetirementAgeOptions } from '~/landing/utils';

import {
  getAllProjections,
  getMaxAmount,
  getRateByRiskProfile,
} from './calculations';
import { RangeSliderInput } from './components/ranger-slider-input';
import { futureSavingsPlannerConstants, riskProfiles } from './constants';

const riskProfileIconMap = {
  CAUTION: 'shieldban',
  BALANCED: 'scale',
  DYNAMISM: 'trending-up',
  BOLDNESS: 'target',
} satisfies Record<RiskProfile, IconName>;

const COLORS = {
  projection: {
    dotBg: 'bg-primary',
    textColor: 'text-primary',
    lineColor: '#262626',
  },
  contribution: {
    dotBg: 'bg-green-500',
    textColor: 'text-green-500',
    lineColor: '#22c55e',
  },
  yield: {
    dotBg: 'bg-accent',
    textColor: 'text-accent',
    lineColor: '#e6e6e6',
  },
};

type Props = {
  blurAmounts?: boolean;
};

export const FutureSavingsPlanner = ({ blurAmounts = false }: Props) => {
  const form = useFormContext<FormSchema>();
  const t = useTranslations('landing');

  const [
    initialDeposit,
    monthlyPayment,
    birthDate,
    retirementAge,
    riskProfile,
  ] = form.watch([
    'futureSavingsPlanner.initialDeposit',
    'futureSavingsPlanner.monthlyPayment',
    'futureSavingsPlanner.birthDate',
    'futureSavingsPlanner.retirementAge',
    'futureSavingsPlanner.riskProfile',
  ]);
  const allProjections = useMemo(
    () =>
      getAllProjections({
        birthDate,
        initialDeposit,
        monthlyPayment,
        retirementAge,
      }),
    [birthDate, initialDeposit, monthlyPayment, retirementAge]
  );

  const maxAmount = useMemo(
    () => getMaxAmount(allProjections),
    [allProjections]
  );

  const retirementOptions = useRetirementAgeOptions({
    dateOfBirth: birthDate,
    isRetired: false,
  });

  const rate = getRateByRiskProfile(riskProfile);

  return (
    <div className="space-y-2">
      <div className="mb-6 flex items-center gap-8">
        <DynamicFontTypography className="text-title2 lg:text-title2desktop">
          {t('germen.retirement.title')}
        </DynamicFontTypography>
        <RippleIndicator content={t('germen.retirement.tooltip.content')} />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SimpleCard
          title={t('retraite.futureSavingsPlanner.form.projection.title')}
        >
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="futureSavingsPlanner.initialDeposit"
              render={({ field }) => (
                <RangeSliderInput
                  className="xl:col-span-full"
                  {...field}
                  helper={t(
                    'retraite.futureSavingsPlanner.form.projection.initialDeposit.helper',
                    {
                      min: formatCurrency(
                        futureSavingsPlannerConstants.initialDeposit.min
                      ),
                      max: formatCurrency(
                        futureSavingsPlannerConstants.initialDeposit.max
                      ),
                    }
                  )}
                  label={t(
                    'retraite.futureSavingsPlanner.form.projection.initialDeposit.label'
                  )}
                  max={futureSavingsPlannerConstants.initialDeposit.max}
                  min={futureSavingsPlannerConstants.initialDeposit.min}
                  step={futureSavingsPlannerConstants.initialDeposit.step}
                />
              )}
            />
            <FormField
              control={form.control}
              name="futureSavingsPlanner.monthlyPayment"
              render={({ field }) => (
                <RangeSliderInput
                  className="xl:col-span-full"
                  {...field}
                  helper={t(
                    'retraite.futureSavingsPlanner.form.projection.monthlyPayment.helper',
                    {
                      min: formatCurrency(
                        futureSavingsPlannerConstants.monthlyPayment.min
                      ),
                      max: formatCurrency(
                        futureSavingsPlannerConstants.monthlyPayment.max
                      ),
                    }
                  )}
                  label={t(
                    'retraite.futureSavingsPlanner.form.projection.monthlyPayment.label'
                  )}
                  max={futureSavingsPlannerConstants.monthlyPayment.max}
                  min={futureSavingsPlannerConstants.monthlyPayment.min}
                  step={futureSavingsPlannerConstants.monthlyPayment.step}
                />
              )}
            />

            <FormField
              control={form.control}
              name="futureSavingsPlanner.birthDate"
              render={({ field }) => (
                <FieldWrapper
                  label={t(
                    'retraite.futureSavingsPlanner.form.projection.birthDate'
                  )}
                >
                  <TextInput
                    className="appearance-none md:appearance-auto"
                    {...field}
                    type="date"
                    onChange={(event) => {
                      field.onChange(event);
                      form.resetField('futureSavingsPlanner.retirementAge');
                    }}
                  />
                </FieldWrapper>
              )}
            />
            <FormField
              control={form.control}
              name="futureSavingsPlanner.retirementAge"
              render={({ field }) => (
                <FieldWrapper
                  label={t(
                    'retraite.futureSavingsPlanner.form.projection.retirementAge'
                  )}
                >
                  <Select
                    aria-label={t(
                      'retraite.futureSavingsPlanner.form.projection.retirementAge'
                    )}
                    {...field}
                    disabled={retirementOptions.length === 0}
                    options={retirementOptions}
                    value={field.value ? String(field.value) : undefined}
                  />
                </FieldWrapper>
              )}
            />
          </div>
          <Separator className="my-6" />

          <div className="mb-6 flex items-center gap-8">
            <Typography variant="title-4">
              {t('retraite.futureSavingsPlanner.form.profile.title')}
            </Typography>
            <RippleIndicator
              content={t(
                'germen.futureSavingsPlanner.rippleIndicators.customRiskProfiles'
              )}
            />
          </div>
          <SimpleCard>
            <FormField
              control={form.control}
              name="futureSavingsPlanner.riskProfile"
              render={({ field }) => (
                <RadioGroup
                  className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  {riskProfiles.map((profile) => (
                    <OptionSelector
                      key={profile}
                      as="radio"
                      label={t(`germen.riskProfile.${profile}.name`)}
                      value={profile}
                    />
                  ))}
                </RadioGroup>
              )}
            />

            <Separator className="my-6" />
            <div className="mb-2 flex items-center gap-2">
              <Icon className="size-6" name={riskProfileIconMap[riskProfile]} />
              <Typography variant="title-6">
                {t(
                  'retraite.futureSavingsPlanner.form.profile.information.title',
                  {
                    riskProfile: t(`germen.riskProfile.${riskProfile}.name`),
                  }
                )}
              </Typography>
            </div>
            <Typography variant="body">
              {t(`germen.riskProfile.${riskProfile}.description`)}
            </Typography>
          </SimpleCard>
        </SimpleCard>

        <SimpleCard className="overflow-x-auto">
          <div className="mb-6 flex items-center gap-8">
            <Typography variant="title-4">
              {t('retraite.futureSavingsPlanner.result.title')}
            </Typography>
            <RippleIndicator
              content={t(
                'germen.futureSavingsPlanner.rippleIndicators.customizeSimulation'
              )}
            />
          </div>
          <div className="relative">
            <div className="absolute right-8 top-36 z-50">
              <RippleIndicator content="Personnalisez les courbes avec vos couleurs" />
            </div>
            <ProjectionChart
              blurAmounts={blurAmounts}
              chartConfig={{
                median: {
                  color: COLORS.projection.lineColor,
                },
                investmentMade: {
                  color: COLORS.contribution.lineColor,
                },
                pessimistic: {
                  color: COLORS.yield.lineColor,
                },
                optimistic: {
                  color: COLORS.yield.lineColor,
                },
              }}
              config={{
                contribution: {
                  colors: COLORS.contribution,
                  legend: t(
                    'retraite.futureSavingsPlanner.result.caption.contribution.retirement'
                  ),
                  explanation: {
                    subtitle: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.contribution.label'
                    ),
                    sentence: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.contribution.content'
                    ),
                  },
                },
                yield: {
                  colors: COLORS.yield,
                  legend: t(
                    'retraite.futureSavingsPlanner.result.caption.yield.retirement'
                  ),
                  explanation: {
                    subtitle: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.yield.label'
                    ),
                    sentence: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.yield.content'
                    ),
                  },
                },
                projection: {
                  colors: COLORS.projection,
                  legend: t(
                    'retraite.futureSavingsPlanner.result.caption.projection.retirement'
                  ),
                  explanation: {
                    subtitle: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.projection.label'
                    ),
                    sentence: t(
                      'retraite.futureSavingsPlanner.result.explanation.GERMEN.projection.content',
                      {
                        rate: formatPercent(rate.median),
                      }
                    ),
                  },
                },
              }}
              explanationsTitle={t(
                'retraite.futureSavingsPlanner.result.explanation.GERMEN.label'
              )}
              maxAmount={maxAmount}
              projections={allProjections[riskProfile]}
              tooltipLabels={{
                investmentMade: t(
                  'retraite.futureSavingsPlanner.result.tooltip.investmentMade.retirement'
                ),
                interestGenerated: t(
                  'retraite.futureSavingsPlanner.result.tooltip.interestGenerated'
                ),
                totalSavings: t(
                  'retraite.futureSavingsPlanner.result.tooltip.savings.atYear'
                ),
                totalSavingsAtLastYear: t(
                  'retraite.futureSavingsPlanner.result.tooltip.savings.atLastYear.retirement'
                ),
              }}
            />
          </div>
        </SimpleCard>
      </div>
      <div className="flex items-center gap-2">
        <Icon className="text-muted-foreground" name="info" size="xs" />
        <Typography className="text-muted-foreground" variant="caption">
          {t('germen.futureSavingsPlanner.disclaimer')}
        </Typography>
      </div>
    </div>
  );
};
